import React, { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TextField, Box, Stack, Tabs, Tab } from '@mui/material'
import { LoadingButton } from '@/components/Button'
import Footer from '@/layout/Footer'
import { useMobileView } from '@/hooks'
import { login } from '@/api/auth'
import { useCapCaptcha } from '@/hooks/useCapCaptcha'
import useCountDown from '@/hooks/useCountDown'
import { AuthCodeInput, VerifyRes } from '../Personal/common'
import { postUserPhoneCode } from '@/api/user'
import { LoginBackground, LoginCard, LoginContent, LoginView, Onboarding } from './components'

export type LoginType = 'wechat' | 'password' | 'wechat_registry'

export const LoginForm: React.FC = () => {
  const isMobile = useMobileView()
  const [searchParams] = useSearchParams()
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [mode, setMode] = useState<'password' | 'phone'>('password')

  const [loginPhone, setLoginPhone] = useState('')
  const [loginCode, setLoginCode] = useState('')
  const [loginCaptchaToken, setLoginCaptchaToken] = useState<string | null>(null)
  const [loginCountDown, startLoginCountDown] = useCountDown('LOGIN_PHONE_COUNT_DOWN')
  const redirect = searchParams?.get('redirect')
  const redirectUrl = redirect ? decodeURIComponent(redirect) : '/console/workbench'

  const { containerRef, start, isReady } = useCapCaptcha()

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setSubmitting(true)
      try {
        if (mode === 'password') {
          if (!account || !password) {
            return
          }
          const token = await start()
          await login({ account, password, kind: 1, token })
        } else {
          if (!loginPhone || !loginCode || !loginCaptchaToken) {
            return
          }
          await login({ account: loginPhone, password: loginCode, kind: 2, token: loginCaptchaToken })
        }
        window.location.href = redirectUrl
      } catch (err) {
      } finally {
        setSubmitting(false)
      }
    },
    [account, password, loginPhone, loginCode, loginCaptchaToken, mode, redirectUrl, start]
  )

  const getLoginPhoneCode = useCallback(
    async (verify: (callback: (p: VerifyRes) => Promise<void>) => void) => {
      if (loginCountDown > 0) return
      if (!loginPhone) return

      const requestCode = (p: VerifyRes) =>
        postUserPhoneCode({ phone: loginPhone, kind: 1, ...p }).then(() => {
          setLoginCaptchaToken(p.token)
          startLoginCountDown()
        })

      await verify(requestCode)
    },
    [loginCountDown, loginPhone, startLoginCountDown]
  )

  return (
    <LoginView>
      {!isMobile && <LoginBackground />}
      {/* @ts-ignore */}
      <LoginContent sx={isMobile && { backgroundColor: '#fff', width: 'auto !important' }}>
        <LoginCard type='password'>
          <Stack component='form' spacing={3} onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <Tabs
              value={mode}
              onChange={(_, v) => setMode(v)}
              variant='fullWidth'
              sx={{ mb: 1 }}
            >
              <Tab label='账号密码登录' value='password' />
              <Tab label='手机号验证码登录' value='phone' />
            </Tabs>

            {mode === 'password' && (
              <>
                <TextField
                  label='邮箱或手机号'
                  size='small'
                  fullWidth
                  value={account}
                  onChange={(e) => setAccount(e.target.value.trim())}
                />
                <TextField
                  label='密码'
                  type='password'
                  size='small'
                  fullWidth
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Box ref={containerRef as React.RefObject<HTMLDivElement>} />
              </>
            )}

            {mode === 'phone' && (
              <>
                <TextField
                  label='手机号'
                  size='small'
                  fullWidth
                  value={loginPhone}
                  onChange={(e) => setLoginPhone(e.target.value.trim())}
                />
                <AuthCodeInput
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value)}
                  label='短信验证码'
                  size='small'
                  fullWidth
                  countDown={loginCountDown}
                  getPhoneCode={getLoginPhoneCode}
                />
              </>
            )}

            <LoadingButton
              loading={submitting}
              disabled={mode === 'password' ? !isReady || submitting : submitting}
              buttonStyle={{ width: '100%' }}
            >
              登录
            </LoadingButton>
          </Stack>
        </LoginCard>
      </LoginContent>
      <Footer />
    </LoginView>
  )
}

const Starter: React.FC = () => {
  const isMobile = useMobileView()
  return (
    <LoginView>
      {!isMobile && <LoginBackground />}
      {/* @ts-ignore */}
      <LoginContent sx={isMobile && { backgroundColor: '#fff', width: 'auto !important' }}>
        <LoginCard type='wechat_registry'>
          <Onboarding />
        </LoginCard>
      </LoginContent>
      <Footer />
    </LoginView>
  )
}
export default LoginForm
export { Starter }
